import { useEffect, useRef } from 'react'
import './ElectricBorder.css'

const padding = 32

// Sample once per resize. The cached smooth rim stays underneath moving arcs.
function contour(width, height, cornerRadius) {
  const inset = 2, radius = Math.max(0, Math.min(cornerRadius, width / 2 - inset, height / 2 - inset))
  const x = padding + inset, y = padding + inset
  const w = width - 2 * inset, h = height - 2 * inset, points = []
  const line = (ax, ay, bx, by, nx, ny) => {
    const steps = Math.ceil(Math.hypot(bx - ax, by - ay) / 2)
    for (let i = 0; i < steps; i++) points.push([ax + (bx - ax) * i / steps, ay + (by - ay) * i / steps, nx, ny])
  }
  const arc = (cx, cy, start) => {
    for (let i = 0; i < 10; i++) {
      const angle = start + Math.PI / 2 * i / 10
      const nx = Math.cos(angle), ny = Math.sin(angle)
      points.push([cx + radius * nx, cy + radius * ny, nx, ny])
    }
  }
  line(x + radius, y, x + w - radius, y, 0, -1); arc(x + w - radius, y + radius, -Math.PI / 2)
  line(x + w, y + radius, x + w, y + h - radius, 1, 0); arc(x + w - radius, y + h - radius, 0)
  line(x + w - radius, y + h, x + radius, y + h, 0, 1); arc(x + radius, y + h - radius, Math.PI / 2)
  line(x, y + h - radius, x, y + radius, -1, 0); arc(x + radius, y + radius, Math.PI)
  return points
}

function trace(ctx, points) {
  ctx.beginPath()
  points.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))
  ctx.closePath()
}

// Seeded, multiscale noise gives the discharge sharp branches, not a sine-wave rim.
function noise(index, seed) {
  const value = Math.sin(index * 127.1 + seed * 311.7) * 43758.5453
  return (value - Math.floor(value)) * 2 - 1
}
function smoothNoise(index, seed) {
  const start = Math.floor(index), part = index - start
  const blend = part * part * (3 - 2 * part)
  return noise(start, seed) * (1 - blend) + noise(start + 1, seed) * blend
}
function offsets(count, seed) {
  return Array.from({ length: count }, (_, i) =>
    smoothNoise(i * .065, seed) * 6 + smoothNoise(i * .24, seed + 3) * 3 + noise(i, seed + 7) * 1.5)
}

// Shared by the unredeemed cover and the post-redemption Telegram offer.
// hostRef must point to a positioned element; the canvas never intercepts clicks.
export function ElectricBorder({ hostRef, active = false, color = '#ffac38', radius = 10 }) {
  const canvas = useRef(null)

  useEffect(() => {
    if (!active || !canvas.current || !hostRef.current || !window.matchMedia) return
    const surface = canvas.current, host = hostRef.current
    const ctx = surface.getContext('2d')
    if (!ctx) return
    const rim = document.createElement('canvas'), glow = rim.getContext('2d')
    if (!glow) return
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    let id = null, visible = false, previous = 0, points = [], width = 0, height = 0
    let noiseFrame = -1, from = [], to = []
    const stop = () => { if (id !== null) cancelAnimationFrame(id); id = null }
    const draw = time => {
      const w = width + padding * 2, h = height + padding * 2
      ctx.clearRect(0, 0, w, h)
      ctx.drawImage(rim, 0, 0, w, h)
      const frame = Math.floor(time / 130), part = time / 130 - frame
      if (noiseFrame !== frame) {
        from = offsets(points.length, frame); to = offsets(points.length, frame + 1)
        noiseFrame = frame
      }
      const live = points.map(([x, y, nx, ny], i) => {
        const offset = from[i] * (1 - part) + to[i] * part
        return [x + nx * offset, y + ny * offset]
      })
      trace(ctx, live)
      ctx.strokeStyle = color; ctx.lineJoin = 'round'; ctx.lineCap = 'round'
      for (const [lineWidth, alpha] of [[8, .12], [4, .3], [1.8, 1]]) {
        ctx.lineWidth = lineWidth; ctx.globalAlpha = alpha; ctx.stroke()
      }
      ctx.strokeStyle = '#f5fcff'; ctx.lineWidth = .7; ctx.globalAlpha = .95; ctx.stroke()

      // Three short forked arcs detach from, then rejoin, the main discharge.
      ctx.beginPath()
      for (let branch = 0; branch < 3; branch++) {
        const start = Math.floor((branch / 3 + time * .000025) % 1 * points.length)
        for (let j = 0; j <= 28; j++) {
          const i = (start + j) % points.length, [, , nx, ny] = points[i]
          const lift = Math.sin(j / 28 * Math.PI) * (7 + noise(i, frame) * 3)
          const x = live[i][0] + nx * lift, y = live[i][1] + ny * lift
          if (j === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
        }
      }
      ctx.strokeStyle = color; ctx.lineWidth = 1.3; ctx.globalAlpha = .9; ctx.stroke()
      ctx.strokeStyle = '#f5fcff'; ctx.lineWidth = .45; ctx.globalAlpha = .75; ctx.stroke()
      ctx.globalAlpha = 1
    }
    const tick = time => {
      if (time - previous >= 1000 / 24) { draw(time); previous = time }
      id = requestAnimationFrame(tick)
    }
    const sync = () => {
      stop()
      if (!visible || document.hidden || motion.matches || !width || !height) {
        ctx.clearRect(0, 0, width + padding * 2, height + padding * 2)
        return
      }
      previous = 0; id = requestAnimationFrame(tick)
    }
    const resize = () => {
      width = host.clientWidth; height = host.clientHeight
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5)
      surface.width = rim.width = Math.round((width + padding * 2) * ratio)
      surface.height = rim.height = Math.round((height + padding * 2) * ratio)
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
      glow.setTransform(ratio, 0, 0, ratio, 0, 0)
      points = contour(width, height, radius); noiseFrame = -1
      // Expensive glow is painted only on resize, never recalculated per frame.
      // Local blooms make the two diagonal corners visibly hotter than the sides.
      for (const [x, y] of [[padding + 7, padding + 7], [width + padding - 7, height + padding - 7]]) {
        const bloom = glow.createRadialGradient(x, y, 0, x, y, 40)
        bloom.addColorStop(0, color + 'b3'); bloom.addColorStop(.2, color + '70')
        bloom.addColorStop(.55, color + '24'); bloom.addColorStop(1, color + '00')
        glow.fillStyle = bloom; glow.fillRect(x - 40, y - 40, 80, 80)
      }
      trace(glow, points)
      glow.strokeStyle = color; glow.shadowColor = color; glow.lineJoin = 'round'
      for (const [blur, lineWidth, alpha] of [[24, 5, .8], [12, 2.5, 1], [3, 1.2, .85]]) {
        glow.shadowBlur = blur; glow.lineWidth = lineWidth; glow.globalAlpha = alpha; glow.stroke()
      }
      // A diagonal gradient lights TL/BR corners more strongly, like the reference.
      const corners = glow.createLinearGradient(padding, padding, width + padding, height + padding)
      corners.addColorStop(0, '#ffffff'); corners.addColorStop(.22, color + '00')
      corners.addColorStop(.78, color + '00'); corners.addColorStop(1, '#ffffff')
      glow.strokeStyle = corners; glow.globalAlpha = 1; glow.lineWidth = 2.5; glow.shadowBlur = 16
      glow.stroke(); glow.shadowBlur = 6; glow.stroke()
      glow.shadowBlur = 0
      sync()
    }
    // Unsupported observer APIs keep the host's original static styling.
    if (!window.IntersectionObserver || !window.ResizeObserver) return
    const intersection = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync() })
    const size = new ResizeObserver(resize)
    intersection.observe(host); size.observe(host); resize()
    document.addEventListener('visibilitychange', sync)
    motion.addEventListener?.('change', sync)
    return () => {
      stop(); intersection.disconnect(); size.disconnect()
      document.removeEventListener('visibilitychange', sync)
      motion.removeEventListener?.('change', sync)
      ctx.clearRect(0, 0, width + padding * 2, height + padding * 2)
    }
  }, [active, color, radius, hostRef])

  return active ? <canvas ref={canvas} className='electric-border__canvas' aria-hidden='true' /> : null
}
